import { Box, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui/components/C1Button/C1Button'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'c1ui/components/C1Dialog/C1Dialog'
import { C1AutocompleteMultipleAsyncHook, C1AutocompleteSingleAsyncHook, SingleSelectionModalProps } from 'components/core/autocomplete'
import { SelectionsModalProps } from 'components/core/autocomplete/C1AutocompleteMultipleAsync'
import { appEntitlementRenderOption as defaultAppEntitlementRenderOption } from 'components/core/search/apps'
import { SelectOneHookProps } from 'components/core/search/index'
import { C1EntitlementDisplayName, getEntitlementDisplayNameTextOnly } from 'components/pages/entitlement/display-name'
import { EntitlementList } from 'components/pages/requestcatalog/details/setup/add_entitlements/list'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppEntitlement, AppEntitlementSearchServiceSearchRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { RequestCatalog, RequestCatalogSearchServiceSearchEntitlementsPerAppRequest, SearchRequestCatalogBundlesRequest } from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import React, { ComponentProps, FC, HTMLAttributes, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { RequestCatalogUserEntitlementPerAppValue } from 'redux_toolkit/browse-access'
import { appEntitlementsSearchApi } from 'redux_toolkit/entitlements'
import { searchRequestCatalogBundlesApi } from 'redux_toolkit/request-catalog-bundles'
import { deprecatedSearchEntitlementsPerAppInCatalogApi } from 'redux_toolkit/request-catalogs'

export const defaultRenderLabel = (option: AppEntitlement): string => option?.displayName || option.id

export const defaultRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: AppEntitlement): React.ReactElement =>
    <li {...props} key={`${option.appId}-${option.id}`}>
        <Box p={1}>
            <C1EntitlementDisplayName entitlement={option} readOnly />
        </Box>
    </li>

export type SearchAppEntitlementsProps = Omit<
    ComponentProps<
        typeof C1AutocompleteMultipleAsyncHook<AppEntitlementSearchServiceSearchRequest, AppEntitlementValue>
    >,
    'Req' | 'hook' | 'initialValue'
> & {
    appEntitlements?: AppEntitlement[]
    appIds?: string[]
    resourceTraitIds?: string[]
    onSelected: (users: AppEntitlement[]) => void
    excludeResourceTypeIds?: string[]
    showEnhancedSearch?: boolean
}

const dialogContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: 'calc(100vh - 250px)',
    overflow: 'hidden',
}

const EntitlementsSelectionsModal: FC<SelectionsModalProps<AppEntitlementValue, AppEntitlementSearchServiceSearchRequest>> = ({
    currentSelections,
    onSelectionChange,
    initialRequest,
    close,
}) => {
    const [selectedEntitlements, setSelectedEntitlements] = useState<AppEntitlementValue[]>(currentSelections)

    const handleSave = (): void => {
        onSelectionChange(selectedEntitlements)
        close()
    }

    const handleCancel = (): void => {
        close()
    }

    const hiddenFilters = [
        ...(initialRequest.appIds && initialRequest.appIds.length > 0
            ? ['app'] as const
            : []
        ),
    ]

    return (
        <C1Dialog maxWidth="lg" fullWidth open onClose={handleCancel}>
            <C1DialogTitle>
                <FormattedMessage
                    id="component_enhanced_search_modal_title"
                    defaultMessage="Select entitlements"
                />
            </C1DialogTitle>
            <C1DialogContent>
                <Stack spacing={2} sx={dialogContentStyles}>
                    <EntitlementList
                        setSelectedEntitlements={setSelectedEntitlements}
                        selectedEntitlements={selectedEntitlements}
                        excludeResourceTypeIds={initialRequest.excludeResourceTypeIds}
                        hiddenFilters={hiddenFilters}
                        selectionMode="multiple"
                    />
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={handleCancel} variant="outlined">
                    <FormattedMessage
                        id="component_enhanced_search_modal_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
                <C1Button onClick={handleSave} variant="contained" color="primary">
                    <FormattedMessage
                        id="component_enhanced_search_modal_save"
                        defaultMessage="Apply selection"
                    />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}

// Single selection modal for entitlements
const SingleEntitlementSelectionsModal: FC<SingleSelectionModalProps<AppEntitlementValue, AppEntitlementSearchServiceSearchRequest>> = ({
    onSelectionChange,
    initialRequest,
    close,
}) => {
    const handleRowClick = (_: React.MouseEvent | React.KeyboardEvent, entitlement: AppEntitlementValue): void => {
        onSelectionChange(entitlement)
        close()
    }

    const handleCancel = (): void => {
        close()
    }

    const hiddenFilters = [
        ...(initialRequest.appIds && initialRequest.appIds.length > 0 ? ['app'] as const : []),
    ]

    return (
        <C1Dialog maxWidth="lg" fullWidth open onClose={handleCancel}>
            <C1DialogTitle>
                <FormattedMessage
                    id="component_single_entitlement_search_modal_title"
                    defaultMessage="Select entitlement"
                />
            </C1DialogTitle>
            <C1DialogContent>
                <Stack spacing={2} sx={dialogContentStyles}>
                    <EntitlementList
                        excludeResourceTypeIds={initialRequest.excludeResourceTypeIds}
                        hiddenFilters={hiddenFilters}
                        selectionMode="single"
                        onRowClick={handleRowClick}
                    />
                </Stack>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={handleCancel} variant="outlined">
                    <FormattedMessage
                        id="component_single_entitlement_search_modal_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}

export const SearchAppEntitlements: FC<SearchAppEntitlementsProps> = ({
    appIds,
    resourceTraitIds,
    appEntitlements,
    renderOption = defaultRenderOption,
    renderLabel = defaultRenderLabel,
    excludeResourceTypeIds,
    showEnhancedSearch = true,
    ...rest
}) => {
    const req = {
        create: (q: Partial<AppEntitlementSearchServiceSearchRequest>) =>
            AppEntitlementSearchServiceSearchRequest.create({
                ...q,
                appIds,
                resourceTraitIds,
                expandMask: { paths: ['*'] },
                excludeResourceTypeIds,
            }),
    }

    return <C1AutocompleteMultipleAsyncHook<AppEntitlementSearchServiceSearchRequest, AppEntitlementValue>
        {...rest}
        initialValue={appEntitlements}
        renderOption={renderOption}
        renderLabel={renderLabel}
        hook={appEntitlementsSearchApi.useLazySearch}
        Req={req}
        SelectionsModal={showEnhancedSearch ? EntitlementsSelectionsModal : undefined}
    />
}

export type RequestCatalogSearchAppEntitlementsProps = Omit<
    ComponentProps<
        typeof C1AutocompleteMultipleAsyncHook<
            RequestCatalogSearchServiceSearchEntitlementsPerAppRequest,
            RequestCatalogUserEntitlementPerAppValue
        >
    >,
    'Req' | 'hook' | 'initialValue' | 'renderOption' | 'renderLabel'
> & {
    initialValue?: RequestCatalogUserEntitlementPerAppValue[]
    appId?: string
    targetUserId?: string
    onSelected: (ents: RequestCatalogUserEntitlementPerAppValue[]) => void
}

export const RequestCatalogSearchAppEntitlements: FC<RequestCatalogSearchAppEntitlementsProps> = ({
    appId,
    targetUserId,
    initialValue,
    ...rest
}) => {
    const req = {
        create: (q: Partial<RequestCatalogSearchServiceSearchEntitlementsPerAppRequest>) =>
            RequestCatalogSearchServiceSearchEntitlementsPerAppRequest.create({
                ...q,
                appId,
                targetUserId,
            }),
    }

    return <C1AutocompleteMultipleAsyncHook<
        RequestCatalogSearchServiceSearchEntitlementsPerAppRequest,
        RequestCatalogUserEntitlementPerAppValue>
        {...rest}
        initialValue={initialValue}
        renderOption={(el, opt): React.ReactNode => {
            if (!opt.entitlement) return ''
            return defaultAppEntitlementRenderOption(el, opt.entitlement)
        }}
        renderLabel={(v): string => {
            if (!v.entitlement) return ''
            return defaultRenderLabel(v.entitlement)
        }}
        hook={deprecatedSearchEntitlementsPerAppInCatalogApi.useLazySearchEntitlementsPerApp}
        Req={req}
    />
}

const appEntitlementRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppEntitlementValue,
): React.ReactElement => (
    <li {...props} key={`${option.appId}-${option.id}`}>
        <C1EntitlementDisplayName entitlement={option} readOnly />
    </li>
)

type SelectOneAppEntitlementHookProps = SelectOneHookProps<AppEntitlementSearchServiceSearchRequest, AppEntitlementValue> & {
    baseRequest?: Partial<AppEntitlementSearchServiceSearchRequest>
    appEntitlement?: AppEntitlementValue
    initAppEntitlementRef?: {
        id: string
        appId: string
    }
    onSelected: (ar: AppEntitlementValue) => void
    showEnhancedSearch?: boolean
    hideLabel?: boolean
}
export const SelectOneAppEntitlementHook: FC<SelectOneAppEntitlementHookProps> = ({
    baseRequest,
    initAppEntitlementRef,
    appEntitlement,
    showEnhancedSearch = true,
    hideLabel = false,
    ...rest
}) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<AppEntitlementSearchServiceSearchRequest>): AppEntitlementSearchServiceSearchRequest => {
            return AppEntitlementSearchServiceSearchRequest.create({ ...q, ...baseRequest })
        },
    }

    return <C1AutocompleteSingleAsyncHook<AppEntitlementSearchServiceSearchRequest, AppEntitlementValue>
        placeholder={intl.formatMessage({ id: 'SelectOneAppEntitlementHook_placeholder', defaultMessage: ' Select an entitlement' })}
        {...rest}
        type={AppEntitlement}
        hook={appEntitlementsSearchApi.useLazySearch}
        reqExpandMask={{ paths: ['*'] }}
        Req={req}
        renderOption={appEntitlementRenderOption}
        renderLabel={getEntitlementDisplayNameTextOnly}
        initialValue={appEntitlement}
        initialRef={initAppEntitlementRef ? AppEntitlementRef.create(initAppEntitlementRef) : undefined}
        SingleSelectionModal={showEnhancedSearch ? SingleEntitlementSelectionsModal : undefined}
        label={!hideLabel ? intl.formatMessage({ id: 'SelectOneAppEntitlementHook_label', defaultMessage: 'Entitlement' }) : undefined}
    />
}

type SelectOneAccessProfileHookProps = SelectOneHookProps<SearchRequestCatalogBundlesRequest, RequestCatalog> & {
    baseRequest?: Partial<SearchRequestCatalogBundlesRequest>
    onSelected: (rc: RequestCatalog) => void
}

export const SelectOneAccessProfileHook: FC<SelectOneAccessProfileHookProps> = ({
    baseRequest,
    ...rest
}) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<SearchRequestCatalogBundlesRequest>): SearchRequestCatalogBundlesRequest => {
            return SearchRequestCatalogBundlesRequest.create({ ...q, ...baseRequest, pageToken: '' })
        },
    }

    return <C1AutocompleteSingleAsyncHook<SearchRequestCatalogBundlesRequest, RequestCatalog>
        {...rest}
        type={RequestCatalog}
        hook={searchRequestCatalogBundlesApi.useLazySearchRequestCatalogBundles}
        placeholder={intl.formatMessage({ id: 'SelectOneAccessProfileHook_placeholder', defaultMessage: 'Select an access profile' })}
        label={intl.formatMessage({ id: 'SelectOneAccessProfileHook_label', defaultMessage: 'Access profile' })}
        isOptionEqualToValue={(option, value): boolean => option.id === value.id}
        renderOption={(props, option): React.ReactNode => {
            return <li {...props} key={option.id}>
                <Typography>{option.displayName}</Typography>
            </li>
        }}
        Req={req}
    />
}
